import { createSupabaseAdminClient } from "@/lib/supabase/admin"
import { getAllRecipes } from "@/data/recipes"

export type DashboardSubmission = {
  id: string
  title: string
  description: string
  status: "pending" | "approved" | "rejected"
  createdAt: string
}

export type DashboardReview = {
  id: string
  recipeId: string
  authorName: string
  rating: number
  text: string
  status: "pending" | "approved" | "rejected"
  createdAt: string
}

export type DashboardComment = {
  id: string
  recipeId: string
  authorName: string
  text: string
  status: "pending" | "approved" | "rejected"
  createdAt: string
}

export type DashboardSavedRecipe = {
  id: string
  recipeId: string
  title: string
  slug: string
  description: string
  savedAt: string
}

export async function getUserDashboardData(userId: string) {
  const supabase = createSupabaseAdminClient()

  const [submissionsResult, reviewsResult, commentsResult, savedRecipesResult] =
    await Promise.all([
      supabase
        .from("recipe_submissions")
        .select("id, title, description, status, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),

      supabase
        .from("recipe_reviews")
        .select("id, recipe_id, author_name, rating, text, status, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),

      supabase
        .from("recipe_comments")
        .select("id, recipe_id, author_name, text, status, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),

      supabase
        .from("saved_recipes")
        .select("id, recipe_id, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false }),
    ])

  const recipes = getAllRecipes()

  const savedRecipes =
    savedRecipesResult.data?.flatMap((row) => {
      const recipe = recipes.find((item) => item.id === row.recipe_id)

      if (!recipe) {
        return []
      }

      return [
        {
          id: row.id,
          recipeId: row.recipe_id,
          title: recipe.title,
          slug: recipe.slug,
          description: recipe.excerpt,
          savedAt: row.created_at,
        },
      ]
    }) ?? []

  return {
    submissions:
      submissionsResult.data?.map((row) => ({
        id: row.id,
        title: row.title,
        description: row.description,
        status: row.status,
        createdAt: row.created_at,
      })) ?? [],

    reviews:
      reviewsResult.data?.map((row) => ({
        id: row.id,
        recipeId: row.recipe_id,
        authorName: row.author_name,
        rating: row.rating,
        text: row.text,
        status: row.status,
        createdAt: row.created_at,
      })) ?? [],

    comments:
      commentsResult.data?.map((row) => ({
        id: row.id,
        recipeId: row.recipe_id,
        authorName: row.author_name,
        text: row.text,
        status: row.status,
        createdAt: row.created_at,
      })) ?? [],

    savedRecipes,
  }
}